import { toast } from "@/components/ui/notify";
import { DRAFT_LIMITS, type DraftParameter, type PairSimProfile } from "@opus-dt/shared-types";
import { getDraftStore } from "@/features/lines/draft/useLineDraftState";
import type { NewParameterInput, ParameterPatch } from "../bridge-types";
import { newParameterToDraft } from "./draft-converters";

export function draftAddParameter(machineId: string, input: NewParameterInput): string {
  const state = getDraftStore().getState();
  const totalParams = Object.values(state.machines).reduce(
    (sum, m) => sum + m.parameters.length,
    0
  );
  if (totalParams >= DRAFT_LIMITS.parameters) {
    const msg = `Máximo de ${DRAFT_LIMITS.parameters} parâmetros por linha atingido.`;
    toast.error(msg);
    throw new Error(msg);
  }
  const machine = state.machines[machineId];
  if (!machine) throw new Error("Máquina não encontrada no draft");
  if (input.category === "STATUS" && machine.parameters.some((p) => p.category === "STATUS")) {
    const msg = "Apenas 1 parâmetro de Status por máquina.";
    toast.error(msg);
    throw new Error(msg);
  }
  return state.addParameter(machineId, newParameterToDraft(input));
}

export function draftUpdateParameter(
  machineId: string,
  paramId: string,
  patch: ParameterPatch
): void {
  getDraftStore()
    .getState()
    .updateParameter(machineId, paramId, patch as Partial<DraftParameter>);
}

export function draftLinkSensor(machineId: string, parameterId: string, sensorId: string): void {
  const state = getDraftStore().getState();
  const totalLinks = Object.values(state.machines).reduce(
    (sum, m) => sum + m.sensorLinks.length,
    0
  );
  if (totalLinks >= DRAFT_LIMITS.sensorLinks) {
    const msg = `Máximo de ${DRAFT_LIMITS.sensorLinks} vínculos de sensores por linha atingido.`;
    toast.error(msg);
    throw new Error(msg);
  }
  state.addSensorLink(machineId, { sensorId, parameterTmpId: parameterId });
}

export function draftSetCompatibility(
  machineId: string,
  productId: string,
  enabled: boolean,
  // Perfil de simulação do par. `undefined` preserva o autorado (mesma tri-state do
  // service: só o `null` explícito limpa) — um toggle de compatibilidade não apaga a autoria.
  simProfile?: PairSimProfile | null
): void {
  const state = getDraftStore().getState();
  if (enabled) {
    const existing = state.machines[machineId]?.compatibilities.find(
      (c) => c.productId === productId
    );
    if (existing) {
      if (simProfile !== undefined) {
        state.updateCompatibility(machineId, existing.tmpId, { simProfile });
      }
      return;
    }
    const totalCompat = Object.values(state.machines).reduce(
      (sum, m) => sum + m.compatibilities.length,
      0
    );
    if (totalCompat >= DRAFT_LIMITS.compatibilities) {
      const msg = `Máximo de ${DRAFT_LIMITS.compatibilities} compatibilidades por linha atingido.`;
      toast.error(msg);
      throw new Error(msg);
    }
    state.addCompatibility(machineId, {
      productId,
      enabled: true,
      ...(simProfile && { simProfile }),
    });
  } else {
    const m = state.machines[machineId];
    const compat = m?.compatibilities.find((c) => c.productId === productId);
    if (compat) state.removeCompatibility(machineId, compat.tmpId);
  }
}
